import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { parseWhatsAppFormatting } from '../utils/whatsappFormatter';
import { formatInArgentina, getDateLabel, isToday } from '../utils/timezone';
import { enUS } from 'date-fns/locale';
import { getPhoneCountryInfo } from '../utils/phoneUtils';

const MessageBubble = ({ message, showDateDivider }) => {
  const isBot = message.sender === 'bot';
  
  // Parse WhatsApp formatting
  const formattedMessage = parseWhatsAppFormatting(message.body || message.message || '');
  
  // Format time in Argentina timezone
  const rawTimeStr = formatInArgentina(message.timestamp || message.message_timestamp, "h:mmaa (O) - EEEE dd/MM/yyyy", { locale: enUS });
  const timeFormatted = rawTimeStr.replace('AM', 'am').replace('PM', 'pm');
  // Use a simple regex that matches the hyphen, space, and the next word character, then capitalizes it.
  const messageTime = timeFormatted.replace(/-\s([a-záéíóúñ])/i, (match, p1) => `- ${p1.toUpperCase()}`);
  const dateLabel = showDateDivider ? getDateLabel(message.timestamp || message.message_timestamp) : null;
  
  // Get participant display name
  const participantPhone = message.from || message.user_from || 'Unknown';
  const phoneInfo = !isBot ? getPhoneCountryInfo(participantPhone) : null;
  const participantDisplay = isBot 
    ? 'GuayerdBot 🤖' 
    : message.participant_name || phoneInfo?.formattedNumber || participantPhone;
  
  return (
    <>
      {showDateDivider && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <Chip 
            label={dateLabel}
            size="small"
            sx={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              color: '#54656F',
              fontSize: '0.75rem',
              height: 24,
              fontWeight: isToday(message.timestamp || message.message_timestamp) ? 600 : 500,
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}
          />
        </Box>
      )}
      
      <Box
        sx={{
          display: 'flex',
          justifyContent: isBot ? 'flex-start' : 'flex-end',
          mb: 2,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            maxWidth: '70%',
            minWidth: 'auto',
            wordBreak: 'break-word',
            bgcolor: isBot ? 'grey.100' : '#0C279F',
            color: isBot ? 'text.primary' : 'white',
            borderRadius: 2,
            borderTopLeftRadius: isBot ? 0 : 16,
            borderTopRightRadius: isBot ? 16 : 0,
            boxShadow: 'none',
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500, 
              mb: 0.5,
              color: isBot ? '#666' : 'rgba(255, 255, 255, 0.9)',
            }}
          >
            {participantDisplay}
          </Typography>
          
          <Typography 
            variant="body1" 
            component="div"
            sx={{ 
              '& strong': {
                fontWeight: 700,
              },
              '& em': {
                fontStyle: 'italic',
              },
              '& del': {
                textDecoration: 'line-through',
              },
              '& code': {
                fontFamily: 'monospace',
                fontSize: '0.9em',
              },
              '& a': {
                color: isBot ? '#0066cc' : '#00CDFF',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }}
            dangerouslySetInnerHTML={{ __html: formattedMessage }}
          />
          
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              mt: 1, 
              opacity: 0.7,
              textAlign: 'right',
              color: isBot ? '#999' : 'rgba(255, 255, 255, 0.7)',
            }}
          >
            {messageTime}
          </Typography>
        </Paper>
      </Box>
    </>
  );
};

export default MessageBubble;